
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Token } from '../../../types';
import { ChainIcon, TokenIcon } from '../../ui/ChainIcons';
import { Copy, CheckCircle } from 'lucide-react';

export const AssetRow: React.FC<{ token: Token }> = ({ token }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(token.mint);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors cursor-pointer group">
           <div className="flex items-center gap-4">
              <div className="relative">
                  <TokenIcon symbol={token.symbol} fallback={token.icon} className="w-12 h-12 shadow-sm group-hover:scale-105 transition-transform" />
                  <div className="absolute -bottom-1 -right-1">
                      <ChainIcon chain={token.chain} className="w-5 h-5 border-2 border-white dark:border-[#15171C] rounded-full" />
                  </div>
              </div>
              <div>
                  <div className="flex items-center gap-2">
                      <div className="font-bold text-[#0A0B0F] dark:text-white leading-none">{token.name}</div>
                      <div 
                          onClick={handleCopy}
                          className="flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded text-[9px] font-mono text-stone-400 hover:text-nobel-gold transition-colors"
                          title="Click to copy contract address"
                      >
                          {token.mint.slice(0, 6)}...{token.mint.slice(-4)}
                          {copied ? <CheckCircle size={8} className="text-emerald-500" /> : <Copy size={8} />}
                      </div>
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1">{token.balance.toLocaleString()} {token.symbol}</div>
              </div>
           </div>
           <div className="text-right">
              <div className="font-bold text-[#0A0B0F] dark:text-white leading-none">${(token.balance * token.priceUsd).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              <div className={`text-[10px] font-bold mt-1 ${token.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {token.change24h > 0 ? '+' : ''}{token.change24h.toFixed(2)}%
              </div>
           </div>
        </div>
    );
};
